import { Link } from 'react-router-dom';
import styles from './Curriculum.module.css';

const STAGES = [
  {
    name: 'Foundation',
    description: 'Core principles and fundamental concepts. Establishes intuition before formalism.',
    level: 'Grades 9-10',
    color: '#2a4a3a',
  },
  {
    name: 'Core',
    description: 'Formal treatment with mathematical rigor. Problem-solving and experimental design.',
    level: 'Grades 11-12',
    color: '#3a4a5a',
  },
  {
    name: 'Advanced',
    description: 'University-level analysis. Numerical methods and research applications.',
    level: 'Undergraduate',
    color: '#4a3a5a',
  },
];

const SUBJECTS = [
  {
    name: 'Physics',
    slug: 'physics',
    topics: ['Mechanics', 'Thermodynamics', 'Electromagnetism', 'Waves & Optics', 'Modern Physics'],
    experiments: 47,
  },
  {
    name: 'Chemistry',
    slug: 'chemistry',
    topics: ['Stoichiometry', 'Equilibrium', 'Kinetics', 'Thermochemistry', 'Electrochemistry'],
    experiments: 38,
  },
  {
    name: 'Biology',
    slug: 'biology',
    topics: ['Cell Biology', 'Genetics', 'Physiology', 'Ecology', 'Evolution'],
    experiments: 32,
  },
  {
    name: 'Mathematics',
    slug: 'mathematics',
    topics: ['Calculus', 'Linear Algebra', 'Differential Equations', 'Statistics', 'Numerical Analysis'],
    experiments: 41,
  },
];

const ALIGNMENT = [
  { board: 'CBSE', codes: ['Class 9-12 Science', 'Class 11-12 Physics, Chemistry, Biology, Mathematics'] },
  { board: 'ICSE', codes: ['Class 9-10 Science', 'Class 11-12 Physics, Chemistry, Biology'] },
  { board: 'Cambridge IGCSE', codes: ['0620 Physics', '0621 Chemistry', '0625 Biology', '0580 Mathematics'] },
  { board: 'AP Physics', codes: ['AP Physics 1', 'AP Physics 2', 'AP Physics C'] },
];

export function Curriculum() {
  return (
    <div className={styles.curriculum}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Curriculum</h1>
          <p className={styles.subtitle}>
            The AxLabs curriculum spans secondary school through undergraduate level,
            organized by progression stages rather than arbitrary grade divisions.
          </p>
        </div>
      </header>

      {/* What Curriculum Means */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>What Curriculum Means in AxLabs</h2>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              Curriculum in AxLabs is not a checklist of topics. It is a structured progression from intuition
              to formalism, from observation to modeling, from concrete to abstract. Each experiment builds on
              prior knowledge and introduces new concepts incrementally.
            </p>
            <p className={styles.proseText}>
              The curriculum is organized around concepts, not chapters. Students encounter phenomena first,
              develop descriptive understanding, then graduate to mathematical models. This mirrors how
              science actually progresses—from observation to theory.
            </p>
          </div>
        </div>
      </section>

      {/* Progression Stages */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Progression Stages</h2>
          <div className={styles.stagesGrid}>
            {STAGES.map((stage, i) => (
              <div key={stage.name} className={styles.stageCard} style={{ borderColor: stage.color + '40' }}>
                <div className={styles.stageNumber}>{String(i + 1).padStart(2, '0')}</div>
                <h3 className={styles.stageName}>{stage.name}</h3>
                <p className={styles.stageLevel}>{stage.level}</p>
                <p className={styles.stageDesc}>{stage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subject-wise Breakdown */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Subject-wise Breakdown</h2>
          <div className={styles.subjectsList}>
            {SUBJECTS.map((subject) => (
              <Link key={subject.slug} to={`/subjects/${subject.slug}`} className={styles.subjectRow}>
                <div className={styles.subjectInfo}>
                  <h3 className={styles.subjectName}>{subject.name}</h3>
                  <p className={styles.subjectExperiments}>{subject.experiments} experiments</p>
                </div>
                <div className={styles.subjectTopics}>
                  {subject.topics.map((topic) => (
                    <span key={topic} className={styles.topicTag}>{topic}</span>
                  ))}
                </div>
                <span className={styles.subjectArrow}>→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Board Alignment */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>School/College Alignment</h2>
          <p className={styles.alignmentNote}>
            AxLabs experiments map to major examination boards. Each experiment tags relevant curriculum codes.
          </p>
          <div className={styles.alignmentGrid}>
            {ALIGNMENT.map((alignment) => (
              <div key={alignment.board} className={styles.alignmentCard}>
                <h3 className={styles.alignmentBoard}>{alignment.board}</h3>
                <ul className={styles.alignmentCodes}>
                  {alignment.codes.map((code) => (
                    <li key={code} className={styles.alignmentCode}>{code}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Philosophy */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Curriculum Philosophy</h2>
          <div className={styles.philosophyGrid}>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Concepts Over Coverage</h3>
              <p className={styles.philosophyText}>
                Deep understanding of fewer topics beats shallow coverage of many. Each experiment targets
                specific conceptual obstacles, not content checkboxes.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Modeling Before Memorization</h3>
              <p className={styles.philosophyText}>
                Students learn to construct and refine models. Memorization follows from understanding
                relationships, not from rote learning.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Failure as Information</h3>
              <p className={styles.philosophyText}>
                Incorrect predictions reveal misconceptions. The curriculum creates opportunities for
                controlled failure and conceptual conflict.
              </p>
            </div>
            <div className={styles.philosophyItem}>
              <h3 className={styles.philosophyTitle}>Reproducibility as Standard</h3>
              <p className={styles.philosophyText}>
                Every experiment can be reproduced exactly. This enables fair assessment and legitimate
                scientific inquiry at all levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <Link to="/" className={styles.footerLink}>Return Home</Link>
          <span className={styles.footerSeparator}>·</span>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
      </footer>
    </div>
  );
}
